import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import { Navigate } from 'react-router-dom'
import { axiosInterceptor } from 'services'
import { API_BASE_URL } from 'services/constants'
import routes from 'constants/routes'
import { Loader2, ShieldAlert } from 'lucide-react'

/**
 * Admin-only support tool: start a read-only impersonation session as another
 * user by supplying their email and the master support password. On success the
 * admin's session is replaced by the target's (impersonation token) and the app
 * reloads as that user; the ImpersonationBanner then offers "Exit".
 */
const SupportImpersonateCN = () => {
	const userInfo = useSelector((state: any) => state.userLogin?.userInfo)

	const [targetEmail, setTargetEmail] = useState('')
	const [masterPassword, setMasterPassword] = useState('')
	const [submitting, setSubmitting] = useState(false)
	const [error, setError] = useState<string | null>(null)

	// Backend enforces admin + master password; this is a client-side guard.
	if (!userInfo?.isAdmin) {
		return <Navigate to={routes.HOME} replace />
	}

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault()
		setError(null)
		if (!targetEmail.trim() || !masterPassword) {
			setError('Target email and master password are required.')
			return
		}
		setSubmitting(true)
		try {
			const api = axiosInterceptor(API_BASE_URL)
			const { data } = await api.post('/support/impersonate', {
				targetEmail: targetEmail.trim(),
				masterPassword,
			})
			// Replace the session with the impersonation session and reload so all
			// app state re-initializes as the target user.
			localStorage.setItem('userInfo', JSON.stringify(data))
			window.location.assign(routes.HOME)
		} catch (err: any) {
			setError(err?.response?.data?.message || err?.message || 'Could not start support session.')
			setSubmitting(false)
		}
	}

	return (
		<div className="mx-auto max-w-md px-4 py-8">
			<div className="mb-6 flex items-center gap-2">
				<ShieldAlert className="h-6 w-6 text-warning" />
				<h1 className="text-2xl font-bold text-gray-900">Support impersonation</h1>
			</div>
			<p className="mb-6 text-sm text-gray-600">
				Sign in as another user to reproduce an issue. The session is <strong>read-only</strong> —
				you can see everything they see but cannot change their data. Every session is audited.
			</p>

			<form onSubmit={handleSubmit} className="space-y-4 rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
				<div className="space-y-2">
					<label htmlFor="s-email" className="text-sm font-medium text-gray-700">
						User email
					</label>
					<input
						id="s-email"
						type="email"
						value={targetEmail}
						onChange={e => setTargetEmail(e.target.value)}
						className="w-full rounded-md border border-input px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
						placeholder="user@company.com"
					/>
				</div>
				<div className="space-y-2">
					<label htmlFor="s-master" className="text-sm font-medium text-gray-700">
						Master password
					</label>
					<input
						id="s-master"
						type="password"
						autoComplete="off"
						value={masterPassword}
						onChange={e => setMasterPassword(e.target.value)}
						className="w-full rounded-md border border-input px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
						placeholder="Support master password"
					/>
				</div>

				{error && <p className="text-sm text-destructive">{error}</p>}

				<button
					type="submit"
					disabled={submitting}
					className="inline-flex items-center justify-center rounded-full bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground transition-colors hover:opacity-90 disabled:opacity-60"
				>
					{submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
					Start support session
				</button>
			</form>
		</div>
	)
}

export default SupportImpersonateCN
